import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from "../components/shared/AuthContext";

/**
 * The date a Receipt or Payment must carry while a day-end blocker stands.
 *
 * Why this exists
 * ---------------
 * The day-end blocker for 08/09 sends the operator to Receipts with a form
 * defaulting to TODAY. Nothing stopped the missing funding being recorded
 * against the wrong day, and that is exactly what happened on branch 1: the
 * 2,50,000 that should have covered 08/09 was receipted on 09/09, re-dated, then
 * ended up on 10/09. The bank read -2,14,300 for two days and the day-end never
 * objected, because availableToLend() ignores dates entirely.
 *
 * While a blocker stands the date is pinned to the blocked day — not past, not
 * future. When it clears the picker returns to normal.
 *
 * Read from the SERVER, not from router state
 * -------------------------------------------
 * The obvious alternative is navigate('/receipts', { state: { lockDate } }), and
 * location.state is idiomatic in this app (Sidebar.jsx:247, Customers.jsx:166).
 * It cannot work here. The lock must be identical whether the operator clicks
 * the popup button or the sidebar link, and the sidebar passes only { entity }.
 * Router state would also vanish on refresh and give two sources of truth that
 * drift. These are the same two endpoints Sidebar.jsx already reads, so the
 * sidebar's lock and this date lock cannot disagree about whether a block
 * stands.
 *
 * @returns {{lockDate: string|null, isLocked: boolean, loading: boolean}}
 *          lockDate is 'YYYY-MM-DD', or null when nothing is blocked.
 */
export default function useVoucherDateLock() {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [lockDate, setLockDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // Superadmin is NOT exempt here, unlike the sidebar lock
      // (Sidebar.jsx:65 `isLocked = isSuperAdmin ? false : ...`). Confirmed with
      // the owner: whoever records the funding for a blocked day must date it to
      // that day, whatever their role. Superadmin keeps its exemption from the
      // separate past-edit rule, which is about correcting history rather than
      // about which day money belongs to.
      if (!branchId) {
        if (!cancelled) {
          setLockDate(null);
          setLoading(false);
        }
        return;
      }

      try {
        const [dayEndRes, fundsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/checkYesterdayDayEnd`, {
            params: { branchId },
          }),
          axios.get(`${API_BASE_URL}/api/ledger/funds-available`, {
            params: { branchId },
          }),
        ]);

        const dayEnd = dayEndRes.data?.data || {};
        const funds = fundsRes.data?.data || {};

        // The same two flags Sidebar.jsx:197-204 unlocks these screens on. If a
        // screen was opened BECAUSE of a block, the date belongs to that block's
        // day.
        const blocked =
          (Array.isArray(funds.unreceiptedAgencies) &&
            funds.unreceiptedAgencies.length > 0) ||
          Number(funds.memberBorrowings) > 0;

        const pendingDate = dayEnd.isPending ? dayEnd.pendingDate || null : null;

        if (!cancelled) {
          // Both halves required: a block with no pending day-end has no day to
          // pin to, and a pending day-end with no block is ordinary business.
          setLockDate(blocked && pendingDate ? pendingDate : null);
          setLoading(false);
        }
      } catch (error) {
        // FAILS OPEN, deliberately — and note this is the opposite of
        // Sidebar.jsx:206, which fails closed.
        //
        // The reasons are opposite too. There, a failed read must not UNLOCK a
        // screen the operator should not reach. Here, a failed read would FREEZE
        // the date picker on a date nobody chose, so an endpoint blip would stop
        // all routine entry. The server still refuses a future date and still
        // enforces the financial year, so nothing unsafe gets through.
        if (!cancelled) {
          setLockDate(null);
          setLoading(false);
        }
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [branchId, isSuperAdmin]);

  return { lockDate, isLocked: Boolean(lockDate), loading };
}
